/* ===========================================================================
   wc-marquee — selected-value scrolling for the mobile variant buttons
   ---------------------------------------------------------------------------
   Two requirements that pull against each other:

     1. Each name scrolls once, rests, then scrolls again.
     2. Two names never scroll at the same time.

   A per-element CSS animation cannot deliver (2): independent animations drift
   into sync sooner or later, and CSS keyframe offsets cannot be computed from a
   variable, so the rest would stretch with the length of the text.

   So timing lives in one shared scheduler that runs every marquee strictly one
   at a time, round-robin, with REST_MS between each. With one marquee that is
   scroll -> rest -> scroll; with two, scroll A -> rest -> scroll B -> rest, so
   they cannot overlap by construction rather than by staying out of phase.

   The buckle value's markup is still built by wc-buckle.js. This file adopts it
   and cancels its CSS animation from script, NOT from a stylesheet -- if this
   file fails to load, that marquee keeps its old continuous scroll rather than
   freezing mid-word.
   =========================================================================== */
(function () {
  'use strict';

  if (window.__wcMarqueeInit) return;
  window.__wcMarqueeInit = true;

  var REST_MS = 3000;        /* pause between one marquee ending and the next starting */
  var FALLBACK_SPEED = 26;   /* px/sec — used only if the buckle marquee's own pace
                                cannot be read (its CSS default is one loop in 8s) */
  var MIN_MS = 2600;
  var MAX_MS = 16000;

  var reduce =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var items = [];
  var idx = 0;
  var timer = null;
  var suppressUntil = 0;
  var pace = 0;              /* px/sec, learned from the existing buckle marquee */

  function valueEls() {
    return document.querySelectorAll('.mobile-variant-btn__value');
  }

  function gapOf(track) {
    var cs = getComputedStyle(track);
    var g = parseFloat(cs.columnGap || cs.gap || '0');
    return isNaN(g) ? 0 : g;
  }

  /* Match the buckle marquee's speed rather than inventing one: read the
     distance and duration wc-buckle.js already published as custom properties
     and divide. Learned once, then applied to every marquee so the colour name
     and the buckle name travel at an identical pace. */
  function learnPace(track) {
    if (pace) return;
    var cs = getComputedStyle(track);
    var secs = parseFloat(cs.getPropertyValue('--wc-marq-time'));
    var dist = parseFloat(cs.getPropertyValue('--wc-marq-dist'));
    if (secs > 0 && dist > 8) pace = dist / secs;
  }

  /* Returns the scrolling track, building one if the text overflows its button.
     Two copies of the text sit side by side, so translating by exactly one copy
     plus the gap lands copy two where copy one began — the reset is invisible. */
  function ensureTrack(el) {
    var track = el.querySelector('.wc-marq');
    if (track) return track;

    var text = (el.textContent || '').trim();
    if (!text) return null;
    if (el.scrollWidth <= el.clientWidth + 1) return null;

    el.textContent = '';
    track = document.createElement('span');
    track.className = 'wc-marq';
    for (var i = 0; i < 2; i++) {
      var t = document.createElement('span');
      t.className = 'wc-marq__t';
      t.textContent = text;
      track.appendChild(t);
    }
    el.appendChild(track);
    el.classList.add('wc-marq-on');
    return track;
  }

  function distanceOf(track) {
    var first = track.querySelector('.wc-marq__t');
    if (!first) return 0;
    return first.getBoundingClientRect().width + gapOf(track);
  }

  function scan() {
    suppressUntil = Date.now() + 200;
    items = [];

    valueEls().forEach(function (el) {
      /* Hidden on desktop, and hidden until its option is chosen. */
      if (el.offsetParent === null) return;

      var track = ensureTrack(el);
      if (!track) {
        el.classList.remove('wc-marq-on');
        return;
      }

      learnPace(track);

      /* Take ownership of the timing. !important because wc-buckle.js may
         re-render this element and reassert its own continuous animation, and
         set here rather than in CSS so a missing script leaves it intact. */
      if (track.style.animationName !== 'none') {
        track.style.setProperty('animation', 'none', 'important');
        track.style.transform = 'translateX(0)';
      }

      var dist = distanceOf(track);
      if (dist < 8) return;

      items.push({ el: el, track: track, dist: dist });
    });
  }

  function runOne(m, done) {
    var speed = pace || FALLBACK_SPEED;
    var ms = Math.min(MAX_MS, Math.max(MIN_MS, (m.dist / speed) * 1000));

    if (!m.track.animate) {
      done();
      return;
    }

    var anim = m.track.animate(
      [
        { transform: 'translateX(0)' },
        { transform: 'translateX(' + -m.dist + 'px)' }
      ],
      { duration: ms, easing: 'linear', fill: 'none' }
    );

    anim.onfinish = function () {
      m.track.style.transform = 'translateX(0)';
      done();
    };
    anim.oncancel = done;
  }

  /* One at a time, in turn. */
  function step() {
    timer = null;

    if (!items.length) {
      timer = setTimeout(step, 900);
      return;
    }

    var m = items[idx % items.length];
    idx++;

    if (!m.el.isConnected || m.el.offsetParent === null) {
      timer = setTimeout(step, 250);
      return;
    }

    runOne(m, function () {
      timer = setTimeout(step, REST_MS);
    });
  }

  function watch() {
    var host =
      document.querySelector('.mobile-variant-buttons-grid') ||
      document.querySelector('variant-selects');

    if (host && !host.__wcMarqWatched) {
      host.__wcMarqWatched = true;
      var pending = null;
      new MutationObserver(function () {
        /* scan() edits the DOM itself; ignore the echo. */
        if (Date.now() < suppressUntil) return;
        clearTimeout(pending);
        pending = setTimeout(scan, 150);
      }).observe(host, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-user-selected', 'class', 'style']
      });
    }

    if (!window.__wcMarqPoll) {
      /* The observer alone was not enough. wc-buckle.js paints its marquee at
         its own moment, and when that landed inside the post-scan suppression
         window the change was ignored and that marquee was never adopted --
         which is why it kept scrolling continuously. A cheap idempotent sweep
         removes the dependency on catching the exact mutation. */
      window.__wcMarqPoll = setInterval(scan, 1500);
    }

    if (!window.__wcMarqResize) {
      window.__wcMarqResize = true;
      var rz = null;
      window.addEventListener('resize', function () {
        clearTimeout(rz);
        rz = setTimeout(scan, 200);
      });
    }
  }

  function boot() {
    if (reduce) return; /* the CSS leaves an ellipsis instead */
    scan();
    watch();
    if (timer === null) timer = setTimeout(step, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    setTimeout(function () {
      scan();
      watch();
    }, 80);
  });
})();
